// ThemeContext — manages light / dark / auto theme switching.
//
//   - "auto" follows the OS via prefers-color-scheme media query.
//   - The user's pick persists to localStorage so it survives reloads.
//   - The effective theme (light | dark) is mirrored as data-theme on
//     <html>; CSS rules in theme.css key off [data-theme="light"] to
//     override the (default-dark) palette variables.
//   - Components can subscribe via useTheme() to flip non-CSS visuals
//     such as the uPlot canvas stroke/grid colors.
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type PropsWithChildren,
} from "react";

export type ThemeMode = "light" | "dark" | "auto";
export type ThemeEffective = "light" | "dark";

interface ThemeCtx {
  mode: ThemeMode;          // user choice
  effective: ThemeEffective; // resolved (auto → light/dark)
  setMode: (m: ThemeMode) => void;
  cycle: () => void;        // light → dark → auto → light
}

const STORAGE_KEY = "sflow.theme";
const Ctx = createContext<ThemeCtx | null>(null);

function readSystemPreference(): ThemeEffective {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light" : "dark";
}

function readStoredMode(): ThemeMode {
  if (typeof window === "undefined") return "dark";
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "light" || v === "dark" || v === "auto" ? v : "dark";
}

export function ThemeProvider({ children }: PropsWithChildren) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [systemPref, setSystemPref] = useState<ThemeEffective>(readSystemPreference);

  // Watch the OS media query so "auto" updates live (e.g. macOS day↔night).
  useEffect(() => {
    if (!window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: light)");
    const handler = () => setSystemPref(mq.matches ? "light" : "dark");
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  const effective: ThemeEffective = mode === "auto" ? systemPref : mode;

  // Mirror to <html data-theme> so CSS overrides apply globally.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", effective);
  }, [effective]);

  const setMode = (m: ThemeMode) => {
    setModeState(m);
    try { localStorage.setItem(STORAGE_KEY, m); } catch { /* ignore */ }
  };

  const cycle = () => {
    setMode(mode === "light" ? "dark" : mode === "dark" ? "auto" : "light");
  };

  const value = useMemo(
    () => ({ mode, effective, setMode, cycle }),
    [mode, effective],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");
  return ctx;
}
